import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card, CardHeader, CardContent } from '../components/Card';
import { Badge } from '../components/Badge';
import { BackgroundSlider } from '../components/BackgroundSlider';
import { ArrowRight, Check, Brain, Clock, Database, Search, FileText, Cpu, Layers, BookOpen, Terminal, Code, Share2, Zap } from 'lucide-react';
interface AIResearchBlueprintPageProps {
  onNavigate?: (path: string) => void;
}
export function AIResearchBlueprintPage({
  onNavigate
}: AIResearchBlueprintPageProps) {
  const modules = [{
    id: 1,
    title: 'AI Fundamentals for Research',
    icon: Brain,
    objectives: ['Understand AI capabilities and limitations', 'Choose the right AI tool for each task', 'Craft effective prompts for research', 'Build ethical AI research practices'],
    tools: ['ChatGPT', 'Claude', 'Perplexity'],
    outcome: 'Foundation for AI-assisted inquiry'
  }, {
    id: 2,
    title: 'ChatGPT Deep Dive',
    icon: Terminal,
    objectives: ['Advanced prompt engineering for research', 'Multi-turn conversations for deep exploration', 'Custom GPTs for specialized research', 'Research workflow automation'],
    tools: ['ChatGPT Plus', 'Custom GPTs'],
    outcome: 'Mastery of conversational research'
  }, {
    id: 3,
    title: 'Gemini Mastery',
    icon: Cpu,
    objectives: ['Multi-modal research (text, images, data)', 'Complex reasoning and analysis', 'Long-context research (1M+ tokens)', 'Data visualization and insights'],
    tools: ['Google Gemini Advanced'],
    outcome: 'Handling complex, multi-modal data'
  }, {
    id: 4,
    title: 'Google NotebookLM',
    icon: BookOpen,
    objectives: ['Knowledge base creation', 'Automatic summarization and synthesis', 'Source-grounded research', 'Audio overviews and study guides'],
    tools: ['NotebookLM'],
    outcome: 'Personalized knowledge synthesis'
  }, {
    id: 5,
    title: 'Perplexity AI for Academic Research',
    icon: Search,
    objectives: ['Citation-backed research', 'Source verification and credibility', 'Academic paper discovery', 'Research question refinement'],
    tools: ['Perplexity Pro'],
    outcome: 'Academic-grade sourcing'
  }, {
    id: 6,
    title: 'Claude for Critical Analysis',
    icon: FileText,
    objectives: ['Long-form document analysis', 'Ethical reasoning and bias detection', 'Constitutional AI principles', 'Research integrity and verification'],
    tools: ['Claude 3 Opus/Sonnet'],
    outcome: 'Deep analytical capability'
  }, {
    id: 7,
    title: 'Research Workflow Automation',
    icon: Zap,
    objectives: ['Build custom research pipelines', 'Automate repetitive research tasks', 'Integrate multiple AI tools', 'Create research dashboards'],
    tools: ['Zapier', 'Make', 'AI Agents'],
    outcome: 'Automated research systems'
  }, {
    id: 8,
    title: 'AI-Powered Note-Taking',
    icon: Code,
    objectives: ['Smart note-taking systems', 'Automatic tagging and linking', 'Knowledge graph creation', 'Spaced repetition with AI'],
    tools: ['Obsidian', 'Notion AI', 'Mem'],
    outcome: 'Second brain construction'
  }, {
    id: 9,
    title: 'Information Synthesis',
    icon: Layers,
    objectives: ['Connect ideas across sources', 'Build personal knowledge bases', 'Generate original insights', 'Create research outputs'],
    tools: ['All Core Tools'],
    outcome: 'Insight generation engine'
  }, {
    id: 10,
    title: 'Future-Proof Learning Strategies',
    icon: Share2,
    objectives: ['Continuous learning systems', 'Staying current with AI advances', 'Building learning habits', 'Career acceleration strategies'],
    tools: ['Feedly AI', 'Curated Sources'],
    outcome: 'Lifelong learning framework'
  }];
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933] font-sans selection:bg-[#00d9ff]/20 selection:text-[#1f2933]" data-id="element-361">
      <Navbar onNavigate={onNavigate} data-id="element-362" />

      {/* Background Slider */}
      <BackgroundSlider data-id="element-363" />

      {/* HERO SECTION */}
      <section className="relative border-b border-dashed border-[#9ca3af] py-20 overflow-hidden bg-[#1f2933] text-[#f5f5f7]" data-id="element-364">
        <div className="absolute inset-0 z-10 bg-[linear-gradient(to_right,#9ca3af10_1px,transparent_1px),linear-gradient(to_bottom,#9ca3af10_1px,transparent_1px)] bg-[size:2rem_2rem]" data-id="element-365" />

        <div className="relative z-20 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-366">
          <Badge variant="secondary" className="mb-6 mx-auto font-mono text-xs tracking-wider border-[#00d9ff] text-[#00d9ff] bg-[#00d9ff]/10" data-id="element-367">
            CURRICULUM_BLUEPRINT
          </Badge>

          <h1 className="mx-auto max-w-4xl text-4xl font-bold tracking-tight sm:text-6xl mb-4" data-id="element-368">
            AI Research Mastery <span className="text-[#00d9ff]" data-id="element-369">2026</span>
          </h1>

          <p className="font-mono text-sm text-[#9ca3af] mb-8 tracking-widest uppercase" data-id="element-370">
            Complete System Architecture
          </p>

          <p className="mx-auto max-w-2xl text-lg font-mono text-[#f5f5f7]/80 mb-8" data-id="element-371">
            A detailed breakdown of the 10-week intensive program designed to
            transform your research capabilities.
          </p>

          <Button size="large" onClick={() => onNavigate && onNavigate('/ai-research-mastery#payment')} className="bg-[#00d9ff] hover:bg-[#00d9ff]/90 border-[#00d9ff] text-[#1f2933]" rightIcon={<ArrowRight className="w-4 h-4" data-id="element-373" />} data-id="element-372">
            ENROLL_NOW
          </Button>
        </div>
      </section>

      {/* PROGRAM OVERVIEW */}
      <section className="py-16 border-b border-dashed border-[#9ca3af]" data-id="element-374">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-375">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center" data-id="element-376">
            <div className="p-6 border border-dashed border-[#9ca3af] bg-white" data-id="element-377">
              <div className="text-3xl font-bold text-[#1f2933] mb-1" data-id="element-378">10</div>
              <div className="font-mono text-xs text-[#9ca3af]" data-id="element-379">
                WEEKS DURATION
              </div>
            </div>
            <div className="p-6 border border-dashed border-[#9ca3af] bg-white" data-id="element-380">
              <div className="text-3xl font-bold text-[#1f2933] mb-1" data-id="element-381">5-7</div>
              <div className="font-mono text-xs text-[#9ca3af]" data-id="element-382">HOURS/WEEK</div>
            </div>
            <div className="p-6 border border-dashed border-[#9ca3af] bg-white" data-id="element-383">
              <div className="text-3xl font-bold text-[#1f2933] mb-1" data-id="element-384">100%</div>
              <div className="font-mono text-xs text-[#9ca3af]" data-id="element-385">
                ONLINE ACCESS
              </div>
            </div>
            <div className="p-6 border border-dashed border-[#9ca3af] bg-white" data-id="element-386">
              <div className="text-3xl font-bold text-[#1f2933] mb-1" data-id="element-387">ALL</div>
              <div className="font-mono text-xs text-[#9ca3af]" data-id="element-388">
                SKILL LEVELS
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES GRID */}
      <section className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-389">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-390">
          <div className="mb-16" data-id="element-391">
            <h2 className="font-mono text-xs font-bold text-[#7c3aed] mb-2" data-id="element-392">
              SYSTEM_MODULES
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933]" data-id="element-393">
              Detailed Curriculum
            </h3>
          </div>

          <div className="grid grid-cols-1 gap-8" data-id="element-394">
            {modules.map(module => <Card key={module.id} className="overflow-hidden border-l-4 border-l-[#1f2933] hover:border-l-[#00d9ff] transition-all duration-300" data-id="element-395">
                <div className="md:flex" data-id="element-396">
                  <div className="md:w-1/4 bg-[#f5f5f7] p-8 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-dashed border-[#9ca3af]" data-id="element-397">
                    <div className="font-mono text-xs text-[#9ca3af] mb-4" data-id="element-398">
                      MODULE_{String(module.id).padStart(2, '0')}
                    </div>
                    <module.icon className="w-12 h-12 text-[#1f2933] mb-4" data-id="element-399" />
                    <div className="font-bold text-center text-[#1f2933]" data-id="element-400">
                      {module.outcome}
                    </div>
                  </div>

                  <div className="md:w-3/4 p-8" data-id="element-401">
                    <h3 className="text-2xl font-bold text-[#1f2933] mb-6" data-id="element-402">
                      {module.title}
                    </h3>

                    <div className="grid md:grid-cols-2 gap-8" data-id="element-403">
                      <div data-id="element-404">
                        <h4 className="font-mono text-xs font-bold text-[#00d9ff] mb-4 uppercase" data-id="element-405">
                          Learning Objectives
                        </h4>
                        <ul className="space-y-2" data-id="element-406">
                          {module.objectives.map((obj, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#1f2933]/80" data-id="element-407">
                              <Check className="w-4 h-4 text-[#00d9ff] flex-shrink-0 mt-0.5" data-id="element-408" />
                              <span data-id="element-409">{obj}</span>
                            </li>)}
                        </ul>
                      </div>

                      <div data-id="element-410">
                        <h4 className="font-mono text-xs font-bold text-[#7c3aed] mb-4 uppercase" data-id="element-411">
                          Tools Covered
                        </h4>
                        <div className="flex flex-wrap gap-2" data-id="element-412">
                          {module.tools.map((tool, i) => <span key={i} className="px-3 py-1 bg-[#1f2933] text-white text-xs font-mono rounded-sm" data-id="element-413">
                              {tool}
                            </span>)}
                        </div>

                        <div className="mt-6 p-4 bg-[#f5f5f7] border border-dashed border-[#9ca3af] rounded-sm" data-id="element-414">
                          <div className="font-mono text-xs text-[#9ca3af] mb-1" data-id="element-415">
                            PRACTICAL_OUTPUT
                          </div>
                          <div className="text-sm font-bold text-[#1f2933]" data-id="element-416">
                            Complete a real-world research project using{' '}
                            {module.tools[0]}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* BONUS MATERIALS */}
      <section className="py-24 border-b border-dashed border-[#9ca3af] bg-[#e5e5e7]" data-id="element-417">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-418">
          <div className="mb-12" data-id="element-419">
            <h2 className="font-mono text-xs font-bold text-[#00d9ff] mb-2" data-id="element-420">
              BONUS_ASSETS
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933]" data-id="element-421">
              Included Resources
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-422">
            {[{
            title: 'AI Tool Matrix',
            desc: 'Comparison guide for 50+ AI research tools'
          }, {
            title: 'Prompt Library',
            desc: '100+ copy-paste templates for research'
          }, {
            title: 'Automation Scripts',
            desc: 'Pre-built workflows for Zapier/Make'
          }, {
            title: 'Community Access',
            desc: 'Lifetime membership to peer network'
          }].map((item, i) => <Card key={i} variant="filled" className="bg-white" data-id="element-423">
                <CardHeader title={item.title} className="font-bold" data-id="element-424" />
                <CardContent data-id="element-425">
                  <p className="font-mono text-sm text-[#1f2933]/70" data-id="element-426">
                    {item.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-32 text-center" data-id="element-427">
        <div className="mx-auto max-w-4xl px-4" data-id="element-428">
          <h2 className="text-4xl font-bold text-[#1f2933] mb-8" data-id="element-429">
            Ready to Master AI Research?
          </h2>

          <p className="text-lg text-[#1f2933]/70 mb-12 max-w-2xl mx-auto" data-id="element-430">
            Join the cohort of forward-thinking students and professionals who
            are defining the future of learning.
          </p>

          <Button size="large" className="min-w-[250px] bg-[#1f2933] hover:bg-[#00d9ff] hover:text-[#1f2933] text-white" onClick={() => onNavigate && onNavigate('/ai-research-mastery#payment')} data-id="element-431">
            ENROLL_NOW
          </Button>
        </div>
      </section>

      <Footer onNavigate={onNavigate} data-id="element-432" />
    </div>;
}